import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import api from '../../utils/api';
import { marketingPreviewBootStorageKey } from '../../utils/marketingPreviewStorage';

function formatPreviewError(err) {
  const msg = err.response?.data?.message || err.message || 'Could not load demo preview.';
  if (err.response?.status === 404 && /endpoint not found/i.test(String(msg))) {
    return `${msg} The backend may need a rebuild/restart to pick up POST /api/public/marketing-preview/session.`;
  }
  return msg;
}

export default function ModulePreviewDialog({ feature, open, onClose, onSignIn }) {
  const isComingSoon = Boolean(feature?.comingSoon);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [iframeSrc, setIframeSrc] = useState('');
  const [viewingAs, setViewingAs] = useState(null);
  const [demoPasswordHint, setDemoPasswordHint] = useState(null);

  useEffect(() => {
    if (!open || !feature?.previewScreen || isComingSoon) {
      setIframeSrc('');
      setError(null);
      setViewingAs(null);
      setDemoPasswordHint(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setIframeSrc('');

      try {
        const screen = feature.previewScreen;
        if (screen === 'public-portal') {
          if (!cancelled) {
            setIframeSrc(`${window.location.origin}/query?embed=1`);
          }
          return;
        }

        const r = await api.post('/public/marketing-preview/session', { screen }, { skipAuthRedirect: true });
        const data = r.data?.data;
        if (!data?.bootId || !data?.token || !data?.path) {
          throw new Error('Invalid preview response from server.');
        }

        const bootKey = marketingPreviewBootStorageKey(data.bootId);
        localStorage.setItem(
          bootKey,
          JSON.stringify({
            token: data.token,
            refreshToken: data.refreshToken || '',
            path: data.path,
            viewingAsLabel: data.viewingAs
              ? `${data.viewingAs.first_name || ''} ${data.viewingAs.last_name || ''} (${data.viewingAs.email})`.trim()
              : null,
          })
        );

        if (!cancelled) {
          setViewingAs(data.viewingAs || null);
          setDemoPasswordHint(data.demoPasswordHint || null);
          setIframeSrc(`${window.location.origin}/marketing-preview-entry/${encodeURIComponent(data.bootId)}`);
        }
      } catch (e) {
        if (!cancelled) {
          setError(formatPreviewError(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, feature?.previewScreen, isComingSoon]);

  const detailsHeading = isComingSoon ? 'What we are building' : 'Capabilities';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isComingSoon ? 'md' : 'xl'}
      fullWidth
      scroll="paper"
      aria-labelledby="module-preview-title"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          ...(isComingSoon
            ? { maxHeight: 'min(92vh, 720px)' }
            : { height: { xs: '92vh', md: '88vh' }, maxHeight: 920 }),
        },
      }}
    >
      <DialogTitle id="module-preview-title" sx={{ py: 2, px: 2.5 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
              {feature.title}
              {!isComingSoon ? ' — live demo preview' : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {isComingSoon
                ? 'ERP4C is the horizontal platform — industry verticals will extend the same core.'
                : 'Real signed-in UI with ABC demo data (same as Admin → Demo / Test Users → View as).'}
            </Typography>
          </Box>
          <IconButton aria-label="Close preview" onClick={onClose} sx={{ mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: isComingSoon ? 'column' : 'row' },
          minHeight: 0,
          flex: 1,
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: isComingSoon ? '100%' : 300 },
            flexShrink: 0,
            p: 2.5,
            borderRight: { md: isComingSoon ? 'none' : '1px solid' },
            borderColor: { md: 'divider' },
            overflow: 'auto',
            bgcolor: 'background.default',
          }}
        >
          <Chip
            label={isComingSoon ? 'Platform roadmap' : 'About this module'}
            size="small"
            color={isComingSoon ? 'secondary' : 'primary'}
            sx={{ mb: 1.5, fontWeight: 700 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            {feature.summary}
          </Typography>
          {isComingSoon && (
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
              {['ERP4Hospital', 'ERP4Hotels', 'ERP4Restaurants', 'ERP4Manufacturing', 'ERP4Doctors'].map((name) => (
                <Chip key={name} label={name} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.72rem' }} />
              ))}
            </Stack>
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {detailsHeading}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
            {feature.details.map((line) => (
              <Typography key={line} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.75, fontSize: '0.85rem', lineHeight: 1.5 }}>
                {line}
              </Typography>
            ))}
          </Box>
          {viewingAs && (
            <Alert severity="info" sx={{ mt: 2, fontSize: '0.8rem' }}>
              Demo login: <strong>{viewingAs.email}</strong>
              {demoPasswordHint ? (
                <>
                  <br />
                  Password: <strong>{demoPasswordHint}</strong>
                </>
              ) : null}
            </Alert>
          )}
        </Box>

        {!isComingSoon && (
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', bgcolor: '#0f172a' }}>
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderBottom: '1px solid rgba(255,255,255,0.08)'
              }}
            >
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {['#ef4444', '#f59e0b', '#22c55e'].map((c) => (
                  <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />
                ))}
              </Box>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace', flex: 1, textAlign: 'center' }}>
                erp4c live demo
              </Typography>
              <Chip label="Sample data" size="small" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700 }} />
            </Box>

            <Box sx={{ flex: 1, position: 'relative', minHeight: 360 }}>
              {loading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', zIndex: 2 }}>
                  <Stack alignItems="center" spacing={1.5}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary">
                      Starting demo session…
                    </Typography>
                  </Stack>
                </Box>
              )}
              {error && !loading && (
                <Box sx={{ p: 3 }}>
                  <Alert severity="warning">{error}</Alert>
                </Box>
              )}
              {iframeSrc && !error && (
                <Box
                  component="iframe"
                  title={`${feature.title} live preview`}
                  src={iframeSrc}
                  sx={{ width: '100%', height: '100%', border: 'none', bgcolor: 'background.default', display: 'block' }}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Close
        </Button>
        {!isComingSoon && (
          <Button variant="contained" startIcon={<OpenInNewIcon />} onClick={onSignIn} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Sign in with your account
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
